<template>
  <div class="container-fluid py-4">
    <!-- Header Row -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h4 class="mb-0">{{ $t('golf.dashboard') }}</h4>
        <small class="text-muted">{{ todayFormatted }}</small>
      </div>
    </div>

    <!-- ===== REGULAR USER VIEW: Hole Grid ===== -->
    <template v-if="!isAdmin">
      <!-- Course selector (only shown when multiple courses exist) -->
      <div v-if="courses.length > 1" class="mb-3">
        <select v-model="selectedCourseId" class="form-select">
          <option v-for="c in courses" :key="c.id" :value="c.id">{{ c.name }}</option>
        </select>
      </div>

      <!-- Hole grid -->
      <p class="text-muted text-center small mb-3">{{ $t('golf.tapHoleToRecord') }}</p>
      <div class="row g-2 mb-4">
        <div v-for="hole in holes" :key="hole.id" class="col-4">
          <button
            class="btn btn-outline-success w-100 hole-btn"
            @click="$router.push(`/golf/hole/${hole.id}`)"
          >
            <div class="fw-bold hole-num">{{ hole.holeNumber }}</div>
            <div v-if="latestClippingMlPerM2(hole) != null" class="small text-muted">{{ latestClippingMlPerM2(hole).toFixed(1) }} mL/m&sup2;</div>
            <div v-if="hole.latestStimpSpeed != null" class="small text-muted">{{ hole.latestStimpSpeed.toFixed(1) }}ft</div>
          </button>
        </div>
      </div>
    </template>

    <!-- ===== ADMIN VIEW: Weather Sync + Row 1 Weather+GDD ===== -->
    <template v-if="isAdmin">

    <!-- Weather Sync Buttons -->
    <div class="row mb-3">
      <div class="col-12">
        <div class="card">
          <div class="card-body py-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <span class="text-muted small fw-bold">{{ $t('golf.weatherSync') }}</span>
            <div class="d-flex gap-2 flex-wrap">
              <button
                class="btn btn-sm btn-outline-primary"
                :disabled="syncing.current"
                @click="syncWeather('current')"
              >
                <i v-if="syncing.current" class="material-icons align-middle spin" style="font-size:16px;">sync</i>
                <i v-else class="material-icons align-middle" style="font-size:16px;">cloud</i>
                {{ $t('golf.syncNow') }}
              </button>
              <button
                class="btn btn-sm btn-outline-info"
                :disabled="syncing.history"
                @click="syncWeather('history')"
              >
                <i v-if="syncing.history" class="material-icons align-middle spin" style="font-size:16px;">sync</i>
                <i v-else class="material-icons align-middle" style="font-size:16px;">history</i>
                {{ $t('golf.sync7DayHistory') }}
              </button>
              <button
                class="btn btn-sm btn-outline-warning"
                :disabled="syncing.forecast"
                @click="syncWeather('forecast')"
              >
                <i v-if="syncing.forecast" class="material-icons align-middle spin" style="font-size:16px;">sync</i>
                <i v-else class="material-icons align-middle" style="font-size:16px;">wb_sunny</i>
                {{ $t('golf.sync5DayForecast') }}
              </button>
              <button
                class="btn btn-sm btn-success"
                :disabled="syncing.current || syncing.history || syncing.forecast"
                @click="syncWeatherAll"
              >
                <i class="material-icons align-middle" style="font-size:16px;">sync</i>
                {{ $t('golf.syncAll') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Row 1: Weather + GDD -->
    <div class="row mb-4">
      <!-- Weather Card -->
      <div class="col-lg-6 mb-3">
        <div class="card h-100">
          <div class="card-body">
            <h6 class="text-uppercase text-muted fw-bold mb-3">{{ $t('golf.todaysWeather') }}</h6>
            <div v-if="weather.currentWeather || todayForecast" class="text-center">
              <!-- Top row: High, Low, Avg temperature -->
              <div class="row mb-3">
                <div class="col-4">
                  <div class="text-danger fw-bold" style="font-size: 2rem;">
                    {{ todayForecast?.highC?.toFixed(1) ?? weather.currentWeather?.temperatureC?.toFixed(1) ?? '–' }}°
                  </div>
                  <small class="text-muted">{{ $t('golf.highTemp') }}</small>
                </div>
                <div class="col-4">
                  <div class="text-primary fw-bold" style="font-size: 2rem;">
                    {{ todayForecast?.lowC?.toFixed(1) ?? '–' }}°
                  </div>
                  <small class="text-muted">{{ $t('golf.lowTemp') }}</small>
                </div>
                <div class="col-4">
                  <div class="text-dark fw-bold" style="font-size: 2rem;">
                    {{ avgTemp ?? '–' }}°
                  </div>
                  <small class="text-muted">{{ $t('golf.avgTemp') }}</small>
                </div>
              </div>
              <!-- Bottom row: Wind, Humidity, Rain -->
              <div class="row">
                <div class="col-4">
                  <div class="fw-bold" style="font-size: 1.4rem;">
                    {{ weather.currentWeather?.windSpeedKmh?.toFixed(0) ?? '–' }}
                    <small class="fw-normal text-muted">km/h</small>
                  </div>
                  <small class="text-muted">{{ $t('golf.wind') }} {{ weather.currentWeather?.windDirection || '' }}</small>
                </div>
                <div class="col-4">
                  <div class="text-info fw-bold" style="font-size: 1.4rem;">
                    {{ weather.currentWeather?.humidity?.toFixed(0) ?? '–' }}%
                  </div>
                  <small class="text-muted">{{ $t('golf.humidity') }}</small>
                </div>
                <div class="col-4">
                  <div class="fw-bold" style="font-size: 1.4rem;">
                    {{ weather.currentWeather?.precipitationMm?.toFixed(1) ?? '0.0' }}
                    <small class="fw-normal text-muted">mm</small>
                  </div>
                  <small class="text-muted">{{ $t('golf.precipitation') }}</small>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-3">
              <small class="text-muted">{{ $t('golf.noWeatherData') }}</small>
            </div>
            <div v-if="weather.todayGDD" class="mt-3 pt-3 border-top text-center">
              <span class="badge bg-info" style="font-size: 0.9rem; padding: 6px 14px;">
                GDD {{ $t('common.today').toLowerCase() }}: {{ weather.todayGDD.toFixed(1) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- GDD Card with 5-day forecast chart -->
      <div class="col-lg-6 mb-3">
        <div class="card h-100">
          <div class="card-body">
            <div class="d-flex align-items-center justify-content-between mb-1">
              <div class="d-flex align-items-center">
                <h6 class="text-uppercase text-muted fw-bold mb-0">{{ $t('golf.gdd') }} (GDD)</h6>
                <i class="material-icons ms-2 text-muted" style="font-size:18px;">trending_up</i>
              </div>
              <button
                v-if="isAdmin"
                class="btn btn-sm btn-outline-secondary"
                :disabled="resettingGdd"
                @click="resetGdd"
              >
                <i v-if="resettingGdd" class="material-icons align-middle spin" style="font-size:16px;">sync</i>
                <i v-else class="material-icons align-middle" style="font-size:16px;">restart_alt</i>
                {{ $t('golf.gddResetButton') }}
              </button>
            </div>
            <small class="text-muted d-block mb-2">
              {{ $t('golf.gddCumulative') }}
              <span v-if="weather.gddLastResetAt">
                · {{ $t('golf.gddLastReset') }}: {{ formatResetDate(weather.gddLastResetAt) }}
              </span>
            </small>

            <!-- True (current) GDD value + progress -->
            <div class="mb-1">
              <small class="text-muted fw-bold">{{ $t('golf.gddTrue') }}</small>
            </div>
            <div class="d-flex align-items-center mb-2">
              <span class="fw-bold me-3" :class="weather.gddAlertActive ? 'text-danger' : 'text-success'" style="font-size: 2.5rem; line-height: 1;">
                {{ weather.cumulativeGDD?.toFixed(0) || '0' }}
              </span>
              <div class="flex-grow-1">
                <div class="progress" style="height: 8px;">
                  <div
                    class="progress-bar"
                    :class="gddProgress >= 100 ? 'bg-danger' : 'bg-success'"
                    role="progressbar"
                    :style="{ width: Math.min(gddProgress, 100) + '%' }"
                  ></div>
                </div>
                <small class="text-muted">Primo Maxx II: 200 GDD</small>
              </div>
            </div>

            <!-- Alert banner -->
            <div v-if="weather.gddAlertActive" class="alert alert-warning py-2 mb-2 d-flex align-items-center">
              <i class="material-icons me-2" style="font-size:20px;">warning</i>
              <span>{{ $t('golf.gddAlert') }}</span>
            </div>

            <!-- 5-day forecasted GDD per-day list -->
            <div v-if="weather.gddForecast && weather.gddForecast.length" class="mb-2">
              <small class="text-muted fw-bold d-block mb-1">{{ $t('golf.gddForecasted') }}</small>
              <div class="d-flex flex-wrap gap-2">
                <div
                  v-for="d in weather.gddForecast"
                  :key="d.date"
                  class="border rounded px-2 py-1 text-center"
                  style="min-width: 70px; flex: 1;"
                >
                  <div class="small text-muted text-capitalize" style="font-size: 0.7rem;">
                    {{ formatForecastDay(d) }}
                  </div>
                  <div class="fw-bold" style="font-size: 0.95rem;">
                    {{ d.dailyGDD.toFixed(1) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- 5-day GDD forecast mini chart -->
            <div v-if="weather.gddForecast && weather.gddForecast.length" style="position: relative; height: 150px;">
              <canvas ref="gddForecastChart"></canvas>
            </div>
            <div v-else class="text-center py-2">
              <small class="text-muted">{{ $t('golf.noForecastData') }}</small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Row 2: Hole Overview Chart -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start mb-3">
              <div>
                <h6 class="fw-bold mb-1">{{ $t('golf.holeOverview') }}</h6>
                <small class="text-muted">{{ $t('golf.holeOverviewHint') }}</small>
              </div>
              <div class="d-flex gap-2">
                <select v-model="selectedCourseId" class="form-select form-select-sm" style="width: auto;">
                  <option v-for="c in courses" :key="c.id" :value="c.id">{{ c.name }}</option>
                </select>
                <select v-model="selectedMetric" class="form-select form-select-sm" style="width: auto;">
                  <option value="clippings">{{ $t('golf.clippingsMlPerM2') }}</option>
                  <option value="stimp">{{ $t('golf.stimp') }}</option>
                </select>
              </div>
            </div>
            <div style="position: relative; height: 350px;">
              <canvas ref="holeChart"></canvas>
            </div>
          </div>
        </div>
      </div>
    </div>

    </template>
    <!-- ===== END ADMIN VIEW ===== -->

    <!-- Row 3: Tasks (visible to all users) -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="mb-3">
              <h6 class="fw-bold mb-1">{{ $t('golf.taskOfTheDay') }}</h6>
              <small class="text-muted">{{ $t('golf.taskStatus') }}</small>
            </div>
            <div v-if="tasks.length === 0" class="text-center py-4">
              <i class="material-icons opacity-50" style="font-size:48px;">check_circle</i>
              <p class="text-muted mt-2">{{ $t('golf.noTasks') }}</p>
            </div>
            <div v-else>
              <div
                v-for="task in tasks"
                :key="task.id"
                class="d-flex align-items-center p-3 mb-2 border rounded"
                :class="{ 'bg-light': task.isCompleted }"
                style="cursor: pointer;"
                @click="toggleTask(task)"
              >
                <i class="material-icons me-3" :class="task.isCompleted ? 'text-success' : 'text-muted'" style="font-size:28px;">
                  {{ task.isCompleted ? 'check_circle' : 'radio_button_unchecked' }}
                </i>
                <div class="flex-grow-1">
                  <strong :class="{ 'text-decoration-line-through text-muted': task.isCompleted }">{{ task.title }}</strong>
                  <div v-if="task.assignedToName" class="small text-muted mt-1">
                    <i class="material-icons align-middle" style="font-size:14px;">person</i> {{ task.assignedToName }}
                  </div>
                </div>
                <span
                  class="badge"
                  :class="task.isCompleted ? 'bg-success' : 'bg-warning text-dark'"
                >
                  {{ task.isCompleted ? $t('golf.completed') : $t('golf.inProgress') }}
                  <span v-if="task.holeNumber"> &middot; #{{ task.holeNumber }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { golfApi as api } from "../../services/api";
import Chart from "chart.js/auto";
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { getCustomerConfig } from '../Settings/customerConfigHelper';
import { clippingMlPerM2 } from '../../utils/clippings';

export default {
  data() {
    return {
      courses: [],
      selectedCourseId: null,
      holes: [],
      tasks: [],
      weather: {
        currentWeather: null,
        fiveDayForecast: [],
        todayGDD: null,
        cumulativeGDD: null,
        gddAlertActive: false,
        gddLastResetAt: null,
        gddForecast: [],
        suggestedActions: [],
      },
      selectedMetric: 'clippings',
      syncing: {
        current: false,
        history: false,
        forecast: false,
      },
      resettingGdd: false,
      defaultGolfCourseId: null,
    };
  },
  computed: {
    isAdmin() {
      try {
        const token = Cookies.get('accessToken');
        if (!token) return false;
        const role = jwtDecode(token).role;
        return role === 'Admin' || role === 'SuperAdmin';
      } catch { return false; }
    },
    todayFormatted() {
      return new Date().toLocaleDateString('is-IS', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
    todayForecast() {
      if (!this.weather.fiveDayForecast || !this.weather.fiveDayForecast.length) return null;
      return this.weather.fiveDayForecast[0];
    },
    avgTemp() {
      const fc = this.todayForecast;
      if (fc && fc.highC != null && fc.lowC != null) {
        return ((fc.highC + fc.lowC) / 2).toFixed(1);
      }
      return null;
    },
    gddProgress() {
      if (!this.weather.cumulativeGDD) return 0;
      return (this.weather.cumulativeGDD / 200) * 100;
    },
  },
  watch: {
    selectedMetric() {
      this.renderChart();
    },
    selectedCourseId() {
      this.fetchHoles();
    },
  },
  async mounted() {
    this._holeChart = null;
    this._gddChart = null;
    const val = await getCustomerConfig('DefaultGolfCourseId');
    this.defaultGolfCourseId = val ? Number(val) : null;
    this.fetchData();
  },
  beforeUnmount() {
    if (this._holeChart) { this._holeChart.stop(); this._holeChart.destroy(); this._holeChart = null; }
    if (this._gddChart) { this._gddChart.stop(); this._gddChart.destroy(); this._gddChart = null; }
  },
  methods: {
    async fetchData() {
      try {
        const [coursesRes, tasksRes] = await Promise.all([
          api.get('/courses'),
          api.get('/tasks'),
        ]);
        this.courses = coursesRes.data;
        this.tasks = tasksRes.data;
        if (this.courses.length > 0 && !this.selectedCourseId) {
          const defaultCourse = this.defaultGolfCourseId
            && this.courses.find(c => c.id === this.defaultGolfCourseId);
          this.selectedCourseId = defaultCourse ? defaultCourse.id : this.courses[0].id;
        } else {
          this.fetchHoles();
        }
      } catch (error) {
        console.error('Failed to load golf dashboard:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      }
      // Weather loaded separately so failures don't block holes/tasks
      try {
        const weatherRes = await api.get('/weather');
        Object.assign(this.weather, weatherRes.data);
        this.$nextTick(() => this.renderGddForecastChart());
      } catch (error) {
        console.error('Failed to load weather:', error); // eslint-disable-line no-console
      }
    },
    async fetchHoles() {
      try {
        const url = this.selectedCourseId ? `/holes?courseId=${this.selectedCourseId}` : '/holes';
        const holesRes = await api.get(url);
        this.holes = holesRes.data;
        this.$nextTick(() => this.renderChart());
      } catch (error) {
        console.error('Failed to load holes:', error); // eslint-disable-line no-console
      }
    },
    renderGddForecastChart() {
      const canvas = this.$refs.gddForecastChart;
      if (!canvas || !canvas.isConnected || !this.weather.gddForecast || !this.weather.gddForecast.length) return;

      const forecast = this.weather.gddForecast;
      const labels = forecast.map(d => d.dayOfWeek || new Date(d.date).toLocaleDateString('is-IS', { weekday: 'short' }));
      const cumulativeValues = forecast.map(d => d.projectedCumulativeGDD);
      const dailyValues = forecast.map(d => d.dailyGDD);

      // Determine colors: green below 200, red at/above 200
      const pointColors = cumulativeValues.map(v => v >= 200 ? 'rgba(220, 53, 69, 1)' : 'rgba(40, 167, 69, 1)');
      const segmentColors = cumulativeValues.map(v => v >= 200 ? 'rgba(220, 53, 69, 0.8)' : 'rgba(40, 167, 69, 0.8)');

      if (this._gddChart) {
        this._gddChart.data.labels = labels;
        this._gddChart.data.datasets[0].data = cumulativeValues;
        this._gddChart.data.datasets[1].data = dailyValues;
        this._gddChart.update('none');
        return;
      }

      this._gddChart = new Chart(canvas.getContext('2d'), {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              type: 'line',
              label: this.$t('golf.gddCumulativeShort'),
              data: cumulativeValues,
              borderColor: segmentColors,
              pointBackgroundColor: pointColors,
              pointRadius: 5,
              pointHoverRadius: 7,
              tension: 0.3,
              fill: false,
              yAxisID: 'y',
              order: 0,
            },
            {
              type: 'bar',
              label: this.$t('golf.gddPerDay'),
              data: dailyValues,
              backgroundColor: 'rgba(13, 110, 253, 0.5)',
              borderColor: 'rgba(13, 110, 253, 0.8)',
              borderWidth: 1,
              yAxisID: 'y1',
              order: 1,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                afterBody: (items) => {
                  const idx = items[0]?.dataIndex;
                  if (idx !== undefined && cumulativeValues[idx] >= 200) {
                    return '⚠ ' + self.$t('golf.gddThreshold');
                  }
                  return '';
                },
              },
            },
            annotation: {
              annotations: {
                primoLine: {
                  type: 'line',
                  yMin: 200,
                  yMax: 200,
                  borderColor: 'rgba(220, 53, 69, 0.6)',
                  borderWidth: 2,
                  borderDash: [6, 4],
                  label: {
                    display: true,
                    content: 'Primo Maxx II (200)',
                    position: 'end',
                    backgroundColor: 'rgba(220, 53, 69, 0.8)',
                    color: '#fff',
                    font: { size: 10 },
                  },
                },
              },
            },
          },
          scales: {
            y: {
              position: 'left',
              title: { display: true, text: this.$t('golf.gddCumulativeShort'), font: { size: 10 } },
              suggestedMin: 0,
            },
            y1: {
              position: 'right',
              title: { display: true, text: this.$t('golf.gddPerDay'), font: { size: 10 } },
              grid: { drawOnChartArea: false },
              suggestedMin: 0,
            },
          },
        },
      });
    },
    renderChart() {
      const canvas = this.$refs.holeChart;
      if (!canvas || !canvas.isConnected || !this.holes.length) return;

      const labels = this.holes.map(h => `${h.holeNumber}`);
      const isClipping = this.selectedMetric === 'clippings';
      const values = this.holes.map(h => (isClipping ? this.latestClippingMlPerM2(h) : h.latestStimpSpeed) ?? null);
      const color = isClipping ? 'rgba(40, 167, 69, 0.7)' : 'rgba(0, 123, 255, 0.7)';
      const label = isClipping ? this.$t('golf.clippingsMlPerM2') : this.$t('golf.stimpSpeed');

      if (this._holeChart) {
        this._holeChart.data.labels = labels;
        this._holeChart.data.datasets[0].data = values;
        this._holeChart.data.datasets[0].backgroundColor = color;
        this._holeChart.data.datasets[0].label = label;
        this._holeChart.update('none');
        return;
      }

      const self = this;
      this._holeChart = new Chart(canvas.getContext('2d'), {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label,
              data: values,
              backgroundColor: color,
              borderRadius: 4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          onClick(_event, elements) {
            if (elements.length > 0) {
              const idx = elements[0].index;
              const hole = self.holes[idx];
              if (hole) self.$router.push(`/golf/hole/${hole.id}`);
            }
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                title: (items) => `${self.$t('golf.hole')} ${items[0].label}`,
                footer: (items) => {
                  const date = self.latestMeasurementDate(self.holes[items[0].dataIndex]);
                  return date ? `${self.$t('golf.measured')}: ${self.formatMeasurementDate(date)}` : '';
                },
              },
            },
          },
        },
      });
    },
    async toggleTask(task) {
      if (task.isCompleted) return;
      try {
        await api.put(`/tasks/${task.id}/complete`);
        task.isCompleted = true;
        task.completedAt = new Date().toISOString();
        this.$toast.success(this.$t('golf.completed'));
      } catch (error) {
        console.error('Failed to complete task:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
      }
    },
    async syncWeather(type) {
      this.syncing[type] = true;
      try {
        await api.post(`/weather/sync/${type}`);
        this.$toast.success(`${type} ${this.$t('golf.syncSuccess')}`);
        const weatherRes = await api.get('/weather');
        Object.assign(this.weather, weatherRes.data);
        this.$nextTick(() => this.renderGddForecastChart());
      } catch (error) {
        console.error(`Failed to sync ${type}:`, error); // eslint-disable-line no-console
        this.$toast.error(`${this.$t('golf.syncError')} (${type})`, { timeout: 0 });
      } finally {
        this.syncing[type] = false;
      }
    },
    async syncWeatherAll() {
      this.syncing.current = true;
      this.syncing.history = true;
      this.syncing.forecast = true;
      try {
        await api.post('/weather/sync');
        this.$toast.success(this.$t('golf.allWeatherSynced'));
        const weatherRes = await api.get('/weather');
        Object.assign(this.weather, weatherRes.data);
        this.$nextTick(() => this.renderGddForecastChart());
      } catch (error) {
        console.error('Failed to sync all weather:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('golf.weatherSyncError'), { timeout: 0 });
      } finally {
        this.syncing.current = false;
        this.syncing.history = false;
        this.syncing.forecast = false;
      }
    },
    async resetGdd() {
      if (!window.confirm(this.$t('golf.gddResetConfirm'))) return;
      this.resettingGdd = true;
      try {
        await api.post('/gdd/reset');
        this.$toast.success(this.$t('golf.gddResetSuccess'));
        const weatherRes = await api.get('/weather');
        Object.assign(this.weather, weatherRes.data);
        this.$nextTick(() => this.renderGddForecastChart());
      } catch (error) {
        console.error('Failed to reset GDD:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
      } finally {
        this.resettingGdd = false;
      }
    },
    latestClippingMlPerM2(hole) {
      return clippingMlPerM2(hole.latestClippingLiters, hole.greenAreaM2);
    },
    latestMeasurementDate(hole) {
      if (!hole) return null;
      return this.selectedMetric === 'clippings' ? hole.latestClippingDate : hole.latestStimpDate;
    },
    formatMeasurementDate(value) {
      return new Date(value).toLocaleDateString('is-IS', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    formatResetDate(value) {
      if (!value) return '';
      return new Date(value).toLocaleDateString('is-IS', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    formatForecastDay(d) {
      if (d.dayOfWeek) return d.dayOfWeek;
      return new Date(d.date).toLocaleDateString('is-IS', { weekday: 'short' });
    },
  },
};
</script>

<style scoped>
.hole-btn {
  min-height: 72px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 4px;
}
.hole-num {
  font-size: 1.4rem;
  line-height: 1.2;
}

.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.form-control,
.form-select {
  border: 1px solid #d2d6da !important;
}

.form-control:focus,
.form-select:focus {
  border-color: #4caf50 !important;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2) !important;
}
</style>
